import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';
import type { UserDataPayload } from '@/services/userService';
import { userService } from '@/services/userService';
import type { User } from '@/types/Api';
import type { NormalizedError } from '@/types/NormalizedError';
import { toAppError } from '@/lib/toAppError';

// Define only the user-specific state management properties
type UserStateManagement = {
  role: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: NormalizedError | null;
};

// Combine User type with state management, making all User properties optional
type UserState = Partial<User> & UserStateManagement;

const initialState: UserState = {
  role: null,
  status: 'idle',
  error: null,
};

export const fetchUserData = createAppThunk<UserDataPayload, { authUserId: string; token: string }>(
  'user/fetchByAuthUserId',
  async ({ authUserId, token }, { rejectWithValue }) => {
    const { getUserByAuthUserId, getAdminRoleById } = userService;
    try {
      if (!authUserId) throw new Error('User ID is not available');

      const response = await getUserByAuthUserId(authUserId, token);

      if (!response) {
        throw new Error('User data could not be fetched.');
      }

      const adminRole = await getAdminRoleById(response.id, token);
      return { ...response, ...adminRole };
    } catch (error: unknown) {
      return rejectWithValue(toAppError(error));
    }
  },
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateUserData: (state, action) => {
      if (!action.payload) return;
      Object.assign(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (!action.payload) {
          return;
        }
        // Update all User properties from payload
        Object.assign(state, action.payload);
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      });
  },
});

export const { updateUserData } = userSlice.actions;
export default userSlice.reducer;
